/**
 * DeckView — read-only modal showing a deck's card list grouped by
 * orbital. Surfaces from DeckBrowser when a player clicks "View" on a
 * tile. Provides the same "Clone & edit" affordance so a player can go
 * straight from "looking at this starter" to "tweaking my copy of it"
 * without bouncing back to the browser grid.
 */

import { type ReactElement, useMemo } from 'react';
import { useCards } from '../hooks/useCards.js';
import type { CardData, DeckData, RealmName } from '../types.js';
import { orbitalColor, REALMS } from '../types.js';

type Props = {
  deck: DeckData;
  onClose: () => void;
  onClone: () => void;
};

type CardEntry = { card: CardData; count: number };

export function DeckView({ deck, onClose, onClone }: Props): ReactElement {
  const cards = useCards();

  const grouped = useMemo(() => {
    const byOrbital = new Map<RealmName | 'Unknown', Map<string, CardEntry>>();
    for (const id of deck.card_ids) {
      const card = cards.cards.get(id);
      const orbital = ((card?.orbital ?? '') as RealmName) || 'Unknown';
      const key: RealmName | 'Unknown' = REALMS.includes(orbital as RealmName)
        ? (orbital as RealmName)
        : 'Unknown';
      let bucket = byOrbital.get(key);
      if (!bucket) {
        bucket = new Map();
        byOrbital.set(key, bucket);
      }
      const existing = bucket.get(id);
      if (existing) existing.count += 1;
      else if (card) bucket.set(id, { card, count: 1 });
      else
        bucket.set(id, {
          card: {
            card_id: id,
            game_id: 'wonders',
            set_id: '',
            name: id,
            raw_text: '',
            parsed_abilities: [],
            ingest_version: '',
            eval_status: '',
          },
          count: 1,
        });
    }
    return byOrbital;
  }, [deck.card_ids, cards.cards]);

  const orderedKeys: (RealmName | 'Unknown')[] = useMemo(() => {
    const out: (RealmName | 'Unknown')[] = [];
    for (const r of REALMS) if (grouped.has(r)) out.push(r);
    if (grouped.has('Unknown')) out.push('Unknown');
    return out;
  }, [grouped]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-cosmos-950/80 p-4 backdrop-blur"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={`Deck contents: ${deck.name}`}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-md border border-cosmos-700 bg-cosmos-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="document"
      >
        <header className="flex items-start justify-between gap-2 border-b border-cosmos-800 p-4">
          <div>
            <h2 className="font-display text-xl text-cosmos-50">{deck.name}</h2>
            <p className="mt-1 text-xs text-cosmos-400">
              <span className="font-mono">{deck.card_ids.length}</span> cards
              {deck.is_tournament ? ' · Starter deck' : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-cosmos-400 hover:text-cosmos-200"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4">
          {orderedKeys.length === 0 ? (
            <p className="text-sm italic text-cosmos-500">No cards.</p>
          ) : (
            <ul className="space-y-4">
              {orderedKeys.map((orbital) => {
                const bucket = grouped.get(orbital);
                if (!bucket) return null;
                const entries = Array.from(bucket.values()).sort((a, b) =>
                  a.card.name.localeCompare(b.card.name),
                );
                const orbitalLabel = orbital === 'Unknown' ? 'Other' : orbital;
                const total = entries.reduce((n, e) => n + e.count, 0);
                return (
                  <li key={orbital}>
                    <div className="mb-2 flex items-center gap-2">
                      <span
                        className={`inline-block h-3 w-3 rounded-full ${orbitalColor(
                          orbital === 'Unknown' ? null : orbital,
                        )}`}
                        aria-hidden
                      />
                      <h3 className="text-[11px] uppercase tracking-wide text-cosmos-300">
                        {orbitalLabel}
                      </h3>
                      <span className="font-mono text-[11px] text-cosmos-500">{total}</span>
                    </div>
                    <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                      {entries.map((entry) => (
                        <li
                          key={entry.card.card_id}
                          className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-cosmos-800"
                        >
                          <img
                            src={cards.imageUrl(entry.card.card_id)}
                            alt=""
                            loading="lazy"
                            className="h-10 w-7 rounded object-cover"
                          />
                          <span className="flex-1 truncate">{entry.card.name}</span>
                          {entry.count > 1 ? (
                            <span className="font-mono text-xs text-cosmos-400">
                              ×{entry.count}
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-cosmos-800 p-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded bg-cosmos-700 px-3 py-1.5 text-sm hover:bg-cosmos-600"
          >
            Close
          </button>
          <button
            type="button"
            onClick={onClone}
            className="rounded bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-emerald-500"
          >
            Clone & edit
          </button>
        </footer>
      </div>
    </div>
  );
}
